import { makeObservable, observable, action, computed, runInAction, reaction } from 'mobx';
import type { RootStore } from '.';
import { db } from './data';
import {
  FirestoreDuplicationRequest,
  DuplicationRequestStatus,
  AccessRequestStatus,
} from '@creative-kit/shared';

export class UiDuplicationRequestStore {
  private rootStore: RootStore;
  private stopListeningToRequests = () => {};
  private stopListeningToRequesterRequests = () => {};
  private reactionDisposer?: () => void;
  duplicationRequests: Record<string, FirestoreDuplicationRequest> = {};
  requesterRequests: Record<string, FirestoreDuplicationRequest> = {}; // Requests made by current user
  private previousRequesterRequestStatuses: Record<string, DuplicationRequestStatus> = {}; // Track previous statuses to detect changes
  isLoading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      duplicationRequests: observable,
      requesterRequests: observable,
      isLoading: observable,
      error: observable,
      pendingRequests: computed,
      myRequests: computed,
      loadDuplicationRequests: action.bound,
      requestDuplication: action.bound,
      approveDuplicationRequest: action.bound,
      denyDuplicationRequest: action.bound,
      cancelDuplicationRequest: action.bound,
      watchDuplicationRequests: action.bound,
      watchRequesterRequests: action.bound,
    });

    this.rootStore = rootStore;
  }

  /**
   * Initialize the store - sets up real-time listeners for duplication requests
   */
  init() {
    // Set up reaction to watch requests when user changes
    // Store the disposer so we can clean it up later
    this.reactionDisposer = reaction(
      () => this.rootStore.authStore.user,
      () => {
        this.watchDuplicationRequests(); // For owners: requests for their sessions
        this.watchRequesterRequests(); // For requesters: their own requests
      },
      { fireImmediately: true },
    );
  }

  /**
   * Get requests made by the current user (for showing status updates)
   */
  get myRequests(): FirestoreDuplicationRequest[] {
    return Object.values(this.requesterRequests);
  }

  get pendingRequests(): Array<{ id: string; request: FirestoreDuplicationRequest }> {
    const { user } = this.rootStore.authStore;
    if (!user) {
      return [];
    }

    // Only return pending requests where:
    // 1. Request is pending
    // 2. Current user owns the session
    // 3. Current user is NOT the requester (can't approve own requests)
    // Return objects with IDs included to avoid race conditions in lookup
    return Object.entries(this.duplicationRequests)
      .filter(
        ([, req]) =>
          req.status === AccessRequestStatus.PENDING &&
          req.sessionOwnerId === user.uid &&
          req.userId !== user.uid,
      )
      .map(([id, req]) => ({ id, request: req }));
  }

  /**
   * Watch duplication requests in real-time (for owners ONLY)
   * Only shows requests for sessions owned by the current user
   * Filters out requests where the user is the requester (they can't approve their own requests)
   */
  watchDuplicationRequests = () => {
    const { user } = this.rootStore.authStore;

    // Stop previous listener
    this.stopListeningToRequests();

    // Clear requests if no user
    // Guest users cannot own sessions or make duplication requests, so exclude them
    // Guest access is temporary and read-only, so duplication functionality is not available
    if (!user || user.isGuest) {
      runInAction(() => {
        this.duplicationRequests = {};
      });
      return;
    }

    // Set up real-time listener for pending requests for sessions owned by this user
    // IMPORTANT: Only show requests where:
    // 1. The current user owns the session (sessionOwnerId == user.uid)
    // 2. The current user is NOT the requester (userId != user.uid)
    // This ensures requesters cannot see/approve their own requests
    const ownerRequestsQuery = db.duplicationRequests
      .where('sessionOwnerId', '==', user.uid)
      .where('status', '==', AccessRequestStatus.PENDING)
      .orderBy('requestedAt', 'desc');

    this.stopListeningToRequests = ownerRequestsQuery.onSnapshot(
      (snapshot) => {
        // Build requests map from snapshot
        // Filter out requests where the current user is the requester
        const requests: Record<string, FirestoreDuplicationRequest> = {};
        snapshot.docs.forEach((doc) => {
          const request = doc.data() as FirestoreDuplicationRequest;
          if (request && request.userId !== user.uid) {
            // Only include requests where the current user is NOT the requester
            requests[doc.id] = request;
          }
        });

        runInAction(() => {
          this.duplicationRequests = requests;
          this.isLoading = false;
        });
      },
      (error) => {
        console.error('Error watching duplication requests:', error);
        runInAction(() => {
          this.error = 'Failed to load duplication requests';
          this.isLoading = false;
        });
      },
    );
  };

  /**
   * Watch requests made by the current user (for requesters to see status updates)
   */
  watchRequesterRequests = () => {
    const { user } = this.rootStore.authStore;

    // Stop previous listener
    this.stopListeningToRequesterRequests();

    // Clear requests if no user
    // Guest users cannot own sessions or make duplication requests, so exclude them
    // Guest access is temporary and read-only, so duplication functionality is not available
    if (!user || user.isGuest) {
      runInAction(() => {
        this.requesterRequests = {};
        this.previousRequesterRequestStatuses = {};
      });
      return;
    }

    // Set up real-time listener for requests made by this user
    const requesterRequestsQuery = db.duplicationRequests
      .where('userId', '==', user.uid)
      .where('status', 'in', [
        AccessRequestStatus.PENDING,
        AccessRequestStatus.APPROVED,
        AccessRequestStatus.DENIED,
      ])
      .orderBy('status')
      .orderBy('requestedAt', 'desc');

    this.stopListeningToRequesterRequests = requesterRequestsQuery.onSnapshot(
      (snapshot) => {
        // Build requests map from snapshot
        const requests: Record<string, FirestoreDuplicationRequest> = {};
        snapshot.docs.forEach((doc) => {
          const request = doc.data() as FirestoreDuplicationRequest;
          if (request) {
            const requestId = doc.id;
            const previousStatus = this.previousRequesterRequestStatuses[requestId];
            const currentStatus = request.status;

            requests[requestId] = request;

            // Only show notification when status changes from PENDING to APPROVED/DENIED
            // This prevents notifications from re-triggering on page refresh
            // On first load, previousStatus will be undefined, so we won't show notifications
            // for already-approved/denied requests
            if (previousStatus !== undefined && previousStatus === AccessRequestStatus.PENDING) {
              if (currentStatus === AccessRequestStatus.APPROVED && request.newSessionId) {
                // Request was just approved - notify requester
                this.rootStore.uiSnackbarStore.setSnackbarDetails({
                  description: `Your duplication request for "${request.sessionName}" was approved! The new session "${request.newSessionName}" is now in your projects.`,
                });
              } else if (currentStatus === AccessRequestStatus.DENIED) {
                // Request was just denied - notify requester
                this.rootStore.uiSnackbarStore.setSnackbarDetails({
                  description: `Your duplication request for "${request.sessionName}" was denied.`,
                });
              }
            }

            // Update tracked status (even if previousStatus was undefined - this initializes it)
            this.previousRequesterRequestStatuses[requestId] = currentStatus;
          }
        });

        // Clean up tracked statuses for requests that no longer exist
        const currentRequestIds = new Set(Object.keys(requests));
        Object.keys(this.previousRequesterRequestStatuses).forEach((requestId) => {
          if (!currentRequestIds.has(requestId)) {
            delete this.previousRequesterRequestStatuses[requestId];
          }
        });

        runInAction(() => {
          this.requesterRequests = requests;
        });
      },
      (error) => {
        console.error('Error watching requester duplication requests:', error);
      },
    );
  };

  /**
   * Load duplication requests for sessions owned by the current user (one-time load)
   */
  async loadDuplicationRequests() {
    const { user } = this.rootStore.authStore;
    if (!user) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      // Query requests for sessions owned by the current user
      // This is more efficient and works with Firestore rules
      const ownerRequestsQuery = db.duplicationRequests
        .where('sessionOwnerId', '==', user.uid)
        .where('status', '==', AccessRequestStatus.PENDING)
        .orderBy('requestedAt', 'desc');

      const snapshot = await ownerRequestsQuery.get();

      // Build requests map
      const requests: Record<string, FirestoreDuplicationRequest> = {};
      snapshot.docs.forEach((doc) => {
        const request = doc.data() as FirestoreDuplicationRequest;
        if (request) {
          requests[doc.id] = request;
        }
      });

      runInAction(() => {
        this.duplicationRequests = requests;
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to load duplication requests';
        console.error('Error loading duplication requests:', error);
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Request duplication of a session (for editors)
   */
  async requestDuplication(
    sessionId: string,
    newSessionName: string,
    duplicationOptions: {
      sources: boolean;
      sourceRows: boolean;
      modules: boolean;
      filenameFormula: boolean;
      queueCards: boolean;
      resetCardStatus: boolean;
    },
    message?: string,
  ) {
    const { user } = this.rootStore.authStore;
    if (!user) {
      throw new Error('User must be authenticated to request duplication');
    }

    // Get session data
    const sessionDoc = await db.sessions.doc(sessionId).get();
    const sessionData = sessionDoc.data();
    if (!sessionData) {
      throw new Error('Session not found');
    }

    // Get session owner ID
    const sessionOwnerId = sessionData.userId;
    if (!sessionOwnerId) {
      throw new Error('Session owner not found');
    }

    this.isLoading = true;
    this.error = null;

    try {
      // Build request data - only include message if provided (not undefined/empty)
      // Firestore doesn't accept undefined values, so we conditionally include message
      const requestData: Omit<FirestoreDuplicationRequest, 'status'> = {
        userId: user.uid,
        email: user.email || '',
        name: user.displayName || user.email || 'Unknown User',
        sessionId,
        sessionName: sessionData.name || 'Untitled Session',
        sessionOwnerId, // Store session owner ID for efficient querying
        newSessionName,
        duplicationOptions,
        requestedAt: new Date(),
      };

      // Only include message if it's provided and not empty
      if (message !== undefined && message !== null && message.trim() !== '') {
        requestData.message = message;
      }

      const docRef = await db.duplicationRequests.add({
        ...requestData,
        status: AccessRequestStatus.PENDING,
      });

      // Build the request object for the store (same structure but with status)
      const requestForStore: FirestoreDuplicationRequest = {
        ...requestData,
        status: AccessRequestStatus.PENDING,
      };
      // Ensure message is included in store object if provided
      if (message !== undefined && message !== null && message.trim() !== '') {
        requestForStore.message = message;
      }

      runInAction(() => {
        this.duplicationRequests[docRef.id] = requestForStore;
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Duplication request sent to project owner',
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to create duplication request';
        console.error('Error creating duplication request:', error);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Approve a duplication request (for owners ONLY)
   */
  async approveDuplicationRequest(requestId: string, responseMessage?: string) {
    const { user } = this.rootStore.authStore;
    if (!user) {
      throw new Error('User must be authenticated to approve duplication request');
    }

    this.isLoading = true;
    this.error = null;

    try {
      const request = this.duplicationRequests[requestId];
      if (!request) {
        throw new Error('Duplication request not found');
      }

      // Ensure user owns the session and is not approving their own request
      if (request.sessionOwnerId !== user.uid) {
        throw new Error('You can only approve requests for sessions you own');
      }
      if (request.userId === user.uid) {
        throw new Error('You cannot approve your own duplication request');
      }

      // Call cloud function to handle the duplication
      // IMPORTANT: Pass targetUserId (requester's userId) so the new session is owned by the requester, not the approver
      const { fn } = await import('firebase-modules');
      const response = await fn.httpsCallable('duplicateSession')({
        sessionId: request.sessionId,
        name: request.newSessionName,
        sources: request.duplicationOptions.sources,
        sourceRows: request.duplicationOptions.sourceRows,
        modules: request.duplicationOptions.modules,
        filenameFormula: request.duplicationOptions.filenameFormula,
        queueCards: request.duplicationOptions.queueCards,
        resetCardStatus: request.duplicationOptions.resetCardStatus,
        targetUserId: request.userId, // Create session for the requester, not the approver
      });

      const data = response.data as { ok: boolean; newSessionId: string };

      if (!data.ok || !data.newSessionId) {
        throw new Error('Failed to duplicate session: Invalid response from server');
      }

      // Update the request with approval status
      // Firestore doesn't accept undefined values, so only include responseMessage if provided
      const updateData: any = {
        status: AccessRequestStatus.APPROVED,
        respondedAt: new Date(),
        respondedBy: {
          userId: user.uid,
          email: user.email,
        },
        newSessionId: data.newSessionId,
        duplicatedAt: new Date(),
      };

      // Only include responseMessage if it's provided (not undefined)
      if (
        responseMessage !== undefined &&
        responseMessage !== null &&
        responseMessage.trim() !== ''
      ) {
        updateData.responseMessage = responseMessage;
      }

      await db.duplicationRequests.doc(requestId).update(updateData);

      runInAction(() => {
        // Remove from pending requests and clear from store
        delete this.duplicationRequests[requestId];
      });

      // Show notification to owner (approver)
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Duplication request approved. Session "${request.newSessionName}" created for ${request.name}.`,
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to approve duplication request';
        console.error('Error approving duplication request:', error);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Deny a duplication request (for owners ONLY)
   */
  async denyDuplicationRequest(requestId: string, responseMessage?: string) {
    const { user } = this.rootStore.authStore;
    if (!user) {
      throw new Error('User must be authenticated to deny duplication request');
    }

    this.isLoading = true;
    this.error = null;

    try {
      const request = this.duplicationRequests[requestId];
      if (!request) {
        throw new Error('Duplication request not found');
      }

      // Ensure user owns the session and is not denying their own request
      if (request.sessionOwnerId !== user.uid) {
        throw new Error('You can only deny requests for sessions you own');
      }
      if (request.userId === user.uid) {
        throw new Error('You cannot deny your own duplication request');
      }

      // Firestore doesn't accept undefined values, so only include responseMessage if provided
      const updateData: any = {
        status: AccessRequestStatus.DENIED,
        respondedAt: new Date(),
        respondedBy: {
          userId: user.uid,
          email: user.email,
        },
      };

      // Only include responseMessage if it's provided (not undefined)
      if (
        responseMessage !== undefined &&
        responseMessage !== null &&
        responseMessage.trim() !== ''
      ) {
        updateData.responseMessage = responseMessage;
      }

      await db.duplicationRequests.doc(requestId).update(updateData);

      runInAction(() => {
        // Remove from pending requests and clear from store
        delete this.duplicationRequests[requestId];
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Duplication request denied`,
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to deny duplication request';
        console.error('Error denying duplication request:', error);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Cancel a duplication request (for requesters)
   */
  async cancelDuplicationRequest(requestId: string) {
    const { user } = this.rootStore.authStore;
    if (!user) {
      throw new Error('User must be authenticated to cancel duplication request');
    }

    this.isLoading = true;
    this.error = null;

    try {
      const request = this.duplicationRequests[requestId];
      if (!request) {
        throw new Error('Duplication request not found');
      }

      if (request.userId !== user.uid) {
        throw new Error('You can only cancel your own duplication requests');
      }

      await db.duplicationRequests.doc(requestId).update({
        status: AccessRequestStatus.CANCELLED,
      });

      runInAction(() => {
        delete this.duplicationRequests[requestId];
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Duplication request cancelled',
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to cancel duplication request';
        console.error('Error cancelling duplication request:', error);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Clean up listeners to prevent memory leaks
   * Should be called when the store is no longer needed (e.g., on app unmount)
   */
  dispose() {
    // Stop the MobX reaction
    this.reactionDisposer?.();

    // Stop all active listeners
    this.stopListeningToRequests();
    this.stopListeningToRequesterRequests();

    // Clear state
    runInAction(() => {
      this.duplicationRequests = {};
      this.requesterRequests = {};
      this.previousRequesterRequestStatuses = {};
      this.isLoading = false;
      this.error = null;
    });
  }
}
